import {
  Component, OnInit, OnDestroy, HostListener,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  trigger, transition, style, animate,
  query, stagger, state
} from '@angular/animations';

import { MaterialModule } from '../../shared/material.module';
import { MenuItemType } from '../../shared/models/menu-item.type';
import { ModuleStateService, SidebarMode } from '../../shared/services/module-state.service';

interface ModuleCard {
  item: MenuItemType;
  description: string;
  icon: string;
}

const MODULE_META: Record<string, { icon: string; description: string }> = {
  dashboard:        { icon: 'dashboard',             description: 'Overview & key metrics at a glance' },
  'my account':     { icon: 'manage_accounts',       description: 'Personal profile & account settings' },
  user:             { icon: 'group',                 description: 'Manage users, approvals & profiles' },
  hierarchy:        { icon: 'account_tree',          description: 'View and manage the MLM network tree' },
  product:          { icon: 'inventory_2',           description: 'Catalogue, SKUs, inventory & pricing' },
  order:            { icon: 'shopping_cart',         description: 'Orders, shipments & returns' },
  wallet:           { icon: 'account_balance_wallet',description: 'Wallet balances, top-ups & payouts' },
  'earnings & wallet': { icon: 'account_balance_wallet', description: 'Wallet balances, top-ups & payouts' },
  'earnings & incentives': { icon: 'star',           description: 'Manage Earnings & Incentives' },
  commission:       { icon: 'percent',               description: 'Commission rules & earned records' },
  bonus:            { icon: 'star',                  description: 'Bonus plans, lists & approvals' },
  reward:           { icon: 'emoji_events',          description: 'Reward points & redemption' },
  report:           { icon: 'bar_chart',             description: 'Business intelligence & analytics' },
  master:           { icon: 'settings',              description: 'System-wide master data configuration' },
  warehouse:        { icon: 'warehouse',             description: 'Warehouses, locations & stock movements' },
  course:           { icon: 'school',                description: 'Training content & e-learning' },
  profile:          { icon: 'manage_accounts',       description: 'Personal profile & account settings' },
  'feature master': { icon: 'tune',                  description: 'Feature flags & access configuration' },
  'rule engine':    { icon: 'rule',                  description: 'Business rule definitions & execution' },
  admin:            { icon: 'admin_panel_settings',  description: 'Manage Admin' },
};

function getMeta(label: string): { icon: string; description: string } {
  const key = label.toLowerCase();
  // Exact match first
  if (MODULE_META[key]) return MODULE_META[key];
  // Partial match
  for (const k of Object.keys(MODULE_META)) {
    if (key.includes(k)) return MODULE_META[k];
  }
  return { icon: 'apps', description: 'Manage ' + label };
}

@Component({
  selector: 'app-nav-modal',
  standalone: true,
  imports: [CommonModule, RouterModule, MaterialModule],
  templateUrl: './nav-modal.component.html',
  styleUrl: './nav-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // Backdrop: fade in with subtle blur-depth scale
    trigger('backdropAnim', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('280ms cubic-bezier(0.4,0,0.2,1)', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms 40ms cubic-bezier(0.4,0,0.2,1)', style({ opacity: 0 }))
      ])
    ]),

    // Panel: spring scale-up from slightly below centre
    trigger('panelAnim', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0.91) translateY(36px)' }),
        animate('400ms cubic-bezier(0.34,1.25,0.64,1)', style({ opacity: 1, transform: 'scale(1) translateY(0)' }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4,0,0.6,1)', style({ opacity: 0, transform: 'scale(0.95) translateY(20px)' }))
      ])
    ]),

    // View slide: pages slides in from right, modules view exits left
    trigger('viewSlideRight', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(56px) scale(0.97)' }),
        animate('340ms cubic-bezier(0.34,1.15,0.64,1)', style({ opacity: 1, transform: 'translateX(0) scale(1)' }))
      ]),
      transition(':leave', [
        style({ position: 'absolute', left: 0, right: 0 }),
        animate('240ms cubic-bezier(0.4,0,0.6,1)', style({ opacity: 0, transform: 'translateX(-56px) scale(0.97)' }))
      ])
    ]),

    // View slide: modules slides back in from left, pages view exits right
    trigger('viewSlideLeft', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-56px) scale(0.97)' }),
        animate('340ms cubic-bezier(0.34,1.15,0.64,1)', style({ opacity: 1, transform: 'translateX(0) scale(1)' }))
      ]),
      transition(':leave', [
        style({ position: 'absolute', left: 0, right: 0 }),
        animate('240ms cubic-bezier(0.4,0,0.6,1)', style({ opacity: 0, transform: 'translateX(56px) scale(0.97)' }))
      ])
    ]),

    // Module/page cards cascade: drop-in with spring bounce + scale
    trigger('staggerCards', [
      transition('* => *', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(24px) scale(0.94)' }),
          stagger('38ms', [
            animate('360ms cubic-bezier(0.34,1.2,0.64,1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
          ])
        ], { optional: true })
      ])
    ]),

    // Dropdown expand: height unfurl with tight spring
    trigger('dropdownExpand', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden', paddingTop: '0', paddingBottom: '0' }),
        animate('360ms cubic-bezier(0.34,1.3,0.64,1)', style({ height: '*', opacity: 1, paddingTop: '*', paddingBottom: '*' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('220ms cubic-bezier(0.4,0,0.6,1)', style({ height: '0', opacity: 0 }))
      ])
    ]),

    // Sub-item cards drop in from slightly above with scale — feels like physical cards settling
    trigger('childStagger', [
      transition('* => *', [
        query(':enter', [
          style({ opacity: 0, transform: 'translateY(-10px) scale(0.96)' }),
          stagger('28ms', [
            animate('300ms cubic-bezier(0.34,1.35,0.64,1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' }))
          ])
        ], { optional: true })
      ])
    ])
  ]
})
export class NavModalComponent implements OnInit, OnDestroy {
  isOpen = false;
  mode: SidebarMode = 'none';
  selectedModule: MenuItemType | null = null;
  moduleCards: ModuleCard[] = [];

  // Track the last open mode for view-slide direction
  previousMode: SidebarMode = 'none';

  private destroy$ = new Subject<void>();

  constructor(
    private moduleStateService: ModuleStateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.moduleStateService.sidebarMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.previousMode = this.mode;
        this.mode = mode;
        this.isOpen = mode !== 'none';
        this.cdr.markForCheck();
      });

    this.moduleStateService.allModules$
      .pipe(takeUntil(this.destroy$))
      .subscribe(modules => {
        this.moduleCards = modules.map(item => ({
          item,
          description: getMeta(item.label).description,
          icon: getMeta(item.label).icon
        }));
        this.cdr.markForCheck();
      });

    this.moduleStateService.selectedModule$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mod => {
        this.selectedModule = mod;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.close();
    }
  }

  close(): void {
    this.moduleStateService.closeSidebar();
  }

  onOverlayClick(): void {
    this.close();
  }

  goBackToModules(): void {
    this.moduleStateService.openModulesSidebar();
  }

  onModuleSelect(card: ModuleCard): void {
    const item = card.item;
    // If the module has no children but has a direct route, navigate immediately
    if (!item.hasChildren && item.route && item.route !== '#') {
      this.router.navigate([item.route]);
      this.close();
      return;
    }
    this.moduleStateService.selectModule(item);
    // selectModule internally calls _sidebarMode.next('pages')
  }

  onPageClick(page: MenuItemType): void {
    if (page.route && page.route !== '#') {
      this.router.navigate([page.route]);
      this.close();
    }
  }

  onSubPageClick(child: MenuItemType): void {
    if (child.route && child.route !== '#') {
      this.router.navigate([child.route]);
      this.close();
    }
  }

  togglePageGroup(page: MenuItemType, event: Event): void {
    event.stopPropagation();
    if (page.hasChildren) {
      page.expanded = !page.expanded;
      this.cdr.markForCheck();
    } else if (page.route && page.route !== '#') {
      this.router.navigate([page.route]);
      this.close();
    }
  }

  getModuleIcon(label: string): string {
    return getMeta(label).icon;
  }

  get selectedModulePages(): MenuItemType[] {
    if (!this.selectedModule) return [];
    return this.selectedModule.children?.filter(c => c.isView) ?? [];
  }

  get slidingIn(): boolean {
    return this.previousMode === 'modules' && this.mode === 'pages';
  }

  get slidingBack(): boolean {
    return this.previousMode === 'pages' && this.mode === 'modules';
  }

  trackByModule(_: number, card: ModuleCard): string {
    return card.item.id;
  }

  trackByPage(_: number, page: MenuItemType): string {
    return page.id;
  }
}
